/**
 * Global Equipment Register Sync Hook
 * Ensures Equipment Register stays in sync with vessel configuration
 * regardless of which module is currently active.
 * 
 * This fixes the issue where Equipment Register sync only works
 * when the user visits the Equipment Records tab.
 */

import { useEffect } from 'react';
import { useVessel } from '../contexts/VesselContext';
import { generateEquipmentRegisterRows } from '../services/equipment/equipmentRegisterMapper';
import { legacyGet, legacySet } from '../storage/legacyUtils';
import { loadUserEquipmentSelections } from '../services/equipment/selectionTransformer';

// Normalize vessel ID to handle quoted strings (same as useEquipmentRegister)
const normalizeVesselId = (id) => {
  if (id == null) return '';
  const s = String(id);
  try { return JSON.parse(s); } catch { return s.replace(/^"|"$/g, ''); }
};

// Schema version for migrations - bump when mapper/system fields change
const REGISTER_SCHEMA_VERSION = 6;

// Storage keys
const LS_KEYS = {
  items: 'equipment_register',
  meta: 'equipment_register_meta'
};

/**
 * Idempotent upsert - merges derived rows with existing, preserving user fields
 */
function upsertById(existing, incoming) {
  const map = new Map(existing.map(item => [item.id, item]));
  const created = [];
  const updated = [];
  const unchanged = [];

  // Process each incoming row
  for (const row of incoming) {
    const prev = map.get(row.id);
    
    if (!prev) {
      // New row
      created.push(row);
      map.set(row.id, row);
      continue;
    }

    // Merge: system fields from new, user fields from prev
    const merged = {
      ...row, // System fields (type, location, spec, etc.)
      // Preserve all user fields
      serialNo: prev.serialNo,
      installedAt: prev.installedAt,
      lastServiceAt: prev.lastServiceAt,
      notes: prev.notes,
      customFields: prev.customFields || {},
      linkedJobIds: prev.linkedJobIds || [],
      photos: prev.photos || [],
      // Keep creation time and source
      createdAt: prev.createdAt,
      source: prev.source, // Don't change source if it's 'added'
      updatedAt: new Date().toISOString(),
      _schemaVersion: REGISTER_SCHEMA_VERSION
    };

    // Check if actually changed (including archived status)
    const systemFieldsChanged = 
      prev.type !== row.type ||
      prev.location !== row.location ||
      prev.archived !== (row.archived || false) || // CRITICAL: Include archived in comparison
      JSON.stringify(prev.spec) !== JSON.stringify(row.spec) ||
      JSON.stringify(prev.sourceRequirementIds) !== JSON.stringify(row.sourceRequirementIds);

    // Unarchive if the derived row wants it active but it's currently archived
    if (prev.archived && !row.archived) {
      merged.archived = false;
      merged.archivedAt = null;
      merged.archivedReason = null;
    }

    if (systemFieldsChanged) {
      updated.push(merged);
      map.set(row.id, merged);
    } else {
      unchanged.push(prev);
    }
  }

  // Archive orphaned requirement-sourced rows
  const incomingIds = new Set(incoming.map(r => r.id));
  const archived = [];
  
  for (const [id, item] of map.entries()) {
    if (!incomingIds.has(id) && 
        !item.archived && 
        item.source === 'requirements') {
      
      // Simple approach: if this exact item ID is not in the new desired set, archive it
      const archivedItem = {
        ...item,
        archived: true,
        archivedAt: new Date().toISOString(),
        archivedReason: 'No longer required by current vessel configuration choices'
      };
      archived.push(archivedItem);
      map.set(id, archivedItem);
    }
  }

  return {
    next: Array.from(map.values()),
    created,
    updated,
    archived,
    unchanged
  };
}

/**
 * Global Equipment Register Sync Hook
 * Call this in the main app to ensure Equipment Register stays synced
 */
export function useGlobalEquipmentSync() {
  const { getCurrentVessel, currentVesselId } = useVessel();
  const currentVessel = getCurrentVessel();

  // Sync from canonical requirements
  const syncFromRequirements = async () => {
    if (!currentVessel || !currentVesselId) {
      return { success: false, error: 'No vessel selected' };
    }

    try {
      // Get rows from canonical requirements
      const derived = generateEquipmentRegisterRows(currentVessel, currentVesselId, 'FFE');

      // Get existing rows from storage (normalize vessel ID)
      const normalizedVesselId = normalizeVesselId(currentVesselId);
      const existing = legacyGet(normalizedVesselId, LS_KEYS.items) || [];

      // Perform idempotent upsert
      const { next, created, updated, archived } = upsertById(existing, derived);

      // Apply changes
      const changeCount = created.length + updated.length + archived.length;
      if (changeCount > 0) {
        // ONE ATOMIC WRITE (use normalized vessel ID)
        legacySet(normalizedVesselId, LS_KEYS.items, next);

      } else {

      }

      return { success: true, created, updated, archived };

    } catch (err) {

      return { success: false, error: err.message };
    }
  };

  // Auto-sync on vessel configuration changes - DISABLED
  // Users MUST use Equipment Selection Tab for complete control
  useEffect(() => {
    if (currentVesselId && currentVessel) {

      // OLD AUTO-POPULATION DISABLED - users must go through Equipment Selection Tab
      // syncFromRequirements(); // COMMENTED OUT
    }
  }, [
    currentVessel?.hasCooking,
    currentVessel?.hasOpenFlame, 
    currentVessel?.galleyHeatSource,
    currentVessel?.openFlameProtection, // No JSON.stringify needed after Fix #1
    currentVessel?.enginePosition,
    currentVessel?.engineBoxProtection, // No JSON.stringify needed after Fix #1
    currentVessel?.engineType,
    currentVesselId
  ]);

  // Schema migration on vessel change - DISABLED
  // Users MUST use Equipment Selection Tab for complete control
  useEffect(() => {
    if (currentVesselId && currentVessel) {

      // OLD SCHEMA MIGRATION DISABLED - users must go through Equipment Selection Tab
      // const normalizedVesselId = normalizeVesselId(currentVesselId);
      // const meta = legacyGet(normalizedVesselId, LS_KEYS.meta) || { schemaVersion: 0 };
      // ... migration logic commented out
    }
  }, [currentVesselId]);

  // Auto-sync on vessel save (using existing vesselUpdated event)
  useEffect(() => {
    const onVesselUpdated = () => {

      // OLD AUTO-POPULATION DISABLED - users must go through Equipment Selection Tab
      // syncFromRequirements(); // COMMENTED OUT
    };
    
    const onManualSync = () => {

      // OLD AUTO-POPULATION DISABLED - users must go through Equipment Selection Tab
      // syncFromRequirements(); // COMMENTED OUT
    };
    
    window.addEventListener('vesselUpdated', onVesselUpdated);
    window.addEventListener('equipment:sync:force', onManualSync);
    
    return () => {
      window.removeEventListener('vesselUpdated', onVesselUpdated);
      window.removeEventListener('equipment:sync:force', onManualSync);
    };
  }, [currentVesselId]); // Only primitive ID needed for event listeners

  return {
    syncFromRequirements
  };
}